import * as React from "react";
import {cx} from "emotion";

import "./sharing-details-form.scss";

import {TextareaAutosize}    from "@Common/libs/react-autosize-textarea/textarea-autosize";
import {bindInput}           from "@Common/react/bind-input";
import {cs}                  from "@Common/react/chain-services";
import {convertState}        from "@Common/react/convert-state";
import {Form2}               from "@Common/react/cs-form-2/form2";
import {required}            from "@Common/react/cs-form-2/validators/required";
import {urlFormatValidation} from "@Common/react/cs-form-2/validators/url-format-validation";
import {scopedState}         from "@Common/react/scoped-state";
import {scnv}                from "@Common/react/state-change-nested-value";
import {Trimmer}             from "@Common/react/trimmer";
import {UseState}            from "@Common/react/use-state";
import {cnv}                 from "@Common/utils/arr-path";
import {
    addDate, sameDate, today, toJsDate_v2, tomorrow
}                            from "@Common/utils/date-object";
import {formatDateUtc_v2}    from "@Common/utils/date/format-date-utc-2";
import {parseDateFormat}     from "@Common/utils/date/parse-date-format";
import {chain, chain2}       from "@Common/utils/fs";
import {omit}                from "@Common/utils/objects";
import {paddingLeft}         from "@Common/utils/strings";

import {QuestionIcon}          from "../../../../../apmui/src/routes/common/components/question-icon";
import {Tooltip}               from "../../../../../apmui/src/routes/common/components/tooltip/tooltip";
import {copyStringToClipboard} from "../../../../../apmui/src/routes/customize/social-media/share-panel/copy-string-to-clipboard";
import {BubbleNoti}            from "../../../bubble-noti/bubble-noti";
import {DatePicker2}           from "../../../date-picker-2/date-picker-2";
import {isDevQuan}             from "../../../debug/is-dev-quan";
import {SliderCheckbox}        from "../../../slider-checkbox/slider-checkbox";
import {formatTime11}          from "../../../time-picker-2/format-time-11";
import {parseTime}             from "../../../time-picker-2/parse-time";
import {TimePicker2}           from "../../../time-picker-2/time-picker-2";
import {RestrictLengthWarning} from "../restrict-length-warning/restrict-length-warning";

export const SharingDetailsForm = ({urlConfig=undefined, initData=undefined, extraComment=undefined, next}) => cs(
    ["data", (_, next) => UseState({
        getInitValue: () => ({
            // url
            ...initData,
            ...isDevQuan() && {
                comment: "Testing",
            },
            schedule: {
                enabled: isDevQuan(),
                date: df.formatDate(today()),
                time: paddingLeft(new Date().getHours() + 1) + ":00",
                ...initData?.schedule,
            },
        }),
        next: (state) => next(chain2(state,[
            urlConfig?.getDefaultValue && (
                (_) => cnv(_, ["value", "url"], (_) => _ ?? urlConfig.getDefaultValue())
            ),
            extraComment?.getDefaultValue && (
                (_) => cnv(_, ["value", extraComment.attr], (_) => _ ?? extraComment.getDefaultValue())
            ),
        ].filter(v=>v))),
    })],
    ["form", ({data}, next) => Form2({
        data,
        showErrors: true,
        fields: {
            url: [urlConfig?.required && required, urlFormatValidation].filter(v=>v),

            comment: [required],
            ...extraComment && data.value?.[extraComment.toggleAttr] && {
                [extraComment.attr]: [required],
            },
            schedule: [
                {
                    validate: (v) => !v?.enabled || (
                        v.date && v.time
                    ),
                },
                {
                    name: "future-time",
                    validate: (v) => !v?.enabled || (
                        toJsDate_v2(df.parseDate(v.date), v.time).getTime() > Date.now()
                    ),
                },
            ],
        },
        next,
    })],
    ({data, form}) => next({
        valid: form.valid,
        highlightErrors: form.highlightErrors,
        rawData: data.value,
        getPostData: () => chain(
            data.value,
            (_) => omit(_, ["schedule"]),
            (_) => !data.value.schedule?.enabled ? _ : {
                ..._,
                date_scheduled: formatDateUtc_v2(toJsDate_v2(df.parseDate(data.value.schedule.date), data.value.schedule.time)),
            }
        ),
        getUrl: () => data.value?.url,
        isSchedulingEnabled: () => data.value.schedule?.enabled,

        renderMain: ({restrictCommentLength, showErrors}) => <>
            {(() => {
                const field = form.field("url");


                // TODO tooltip: socialMediaStatus['instagram'] && "The Listing URL does not apply to Instagram",

                const required = field.validators.find((v) => v.name === "required");
                const label = urlConfig?.label ?? "Post URL";
                return (
                    <div className={cx("sharing-details-form-38m line", !required ? "optional" : "required", {hasError: showErrors && field.error})}>
                        <div
                            className="left"
                            xd-qa-type="label" xd-qa-name={label}
                        >
                            {label}
                        </div>

                        <div className="right url">

                            {cs(

                                ["buffer", (_, next) => Trimmer({
                                    state: field.state,
                                    next,
                                })],
                                ({buffer}) => (
                                    <input {...{
                                        ...bindInput(buffer.state),
                                        ref: field.ref,
                                        className: field.errCss,
                                        onBlur: buffer.flush,

                                        "xd-qa-type": "text_field",
                                        "xd-qa-name": label,
                                    }}/>
                                )
                            )}

                            {urlConfig?.tooltip && cs(
                                ["tooltip", (_, next) => Tooltip({
                                    message: urlConfig.tooltip,
                                    next,
                                })],
                                ({tooltip}) => (
                                    <div
                                        className="tooltip" ref={tooltip.targetRef}
                                        {...tooltip.control}
                                        xd-qa-type="tooltip"
                                        xd-qa-name={urlConfig.tooltip}
                                    >
                                        {QuestionIcon()}
                                    </div>
                                )
                            )}

                        </div>
                    </div>
                );
            })()}
            {(() => {
                const field = form.field("comment");
                return (
                    <div className={cx("sharing-details-form-38m line required", {hasError: showErrors && field.error})}>
                        <div
                            className="left"
                            xd-qa-type="label" xd-qa-name={"Post Comment"}
                        >
                            Post Comment
                        </div>

                        <div className="right">
                            {cs(
                                ["buffer", (_, next) => Trimmer({
                                    state: field.state,
                                    next,
                                })],
                                ({buffer}) => (
                                    <TextareaAutosize {...{
                                        style: {resize: "vertical"},
                                        className: field.errCss,
                                        ...bindInput(buffer.state),
                                        onBlur: buffer.flush,
                                        ref: field.ref,

                                        "xd-qa-type": "text_field",
                                        "xd-qa-name": "Post Comment",
                                    }}/>
                                )
                            )}

                            {restrictCommentLength && (
                                RestrictLengthWarning({
                                    restrict: restrictCommentLength,
                                    currentLength: field.state.value?.length ?? 0,
                                    xdQaName: "Post Comment",
                                })
                            )}
                        </div>
                    </div>
                );
            })()}

            {extraComment && (() => {
                const field = form.field(extraComment.attr);
                const enabled = data.value?.[extraComment.toggleAttr];
                const required = field.validators?.find((v) => v.name === "required");
                return (
                    <div className={cx("sharing-details-form-38m line", !required ? "optional" : "required", {hasError: showErrors && field.error})}>
                        <div
                            className="left"
                            xd-qa-type="label" xd-qa-name={extraComment.label}
                        >
                            {extraComment.label}
                        </div>

                        <div className="right extra-comment">
                            <div className="line1">
                                {SliderCheckbox({
                                    value: enabled,
                                    xdQaName: "Include Listing Description",
                                    onChange: () => scnv(form.field(extraComment.toggleAttr).state, [], v=>!v),
                                })}

                                {cs(
                                    ["tooltip", (_, next) => Tooltip({
                                        message: extraComment.tooltip,
                                        next,
                                    })],
                                    ({tooltip}) => (
                                        <div
                                            className="tooltip" ref={tooltip.targetRef}
                                            {...tooltip.control}
                                            xd-qa-type="tooltip"
                                            xd-qa-name={extraComment.label}
                                        >
                                            {QuestionIcon()}
                                        </div>
                                    )
                                )}
                            </div>

                            {cs(
                                ["buffer", (_, next) => Trimmer({
                                    state: field.state,
                                    next,
                                })],
                                ({buffer}) => (
                                    <TextareaAutosize {...{
                                        style: {resize: "vertical"},
                                        className: field.errCss,
                                        disabled: !enabled,
                                        ...bindInput(buffer.state),
                                        ref: field.ref,
                                        onBlur: buffer.flush,

                                        "xd-qa-type": "text_field",
                                        "xd-qa-name": extraComment.label,
                                    }}/>
                                )
                            )}

                            {cs(
                                ["noti", (_, next) => BubbleNoti({next})],
                                ({noti}) => (
                                    <div className="copy">
                                        <button
                                            onClick={() => {
                                                copyStringToClipboard(field.state.value);
                                                noti.show({message: "Copied to Clipboard", color: "green"});
                                            }}
                                            xd-qa-type="button"
                                            xd-qa-name="Copy to clipboard"
                                        >
                                            <i className="fa fa-copy"/>
                                            Copy to clipboard
                                        </button>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                );
            })()}

        </>,

        renderScheduler: ({showErrors}) => {
            const field = form.field("schedule");

            const enabled = field.state.value?.enabled;
            return (
                <div
                    className={cx("sharing-details-form-38m line", {hasError: false})}
                    xd-qa-type="card"
                    xd-qa-name="Schedule for Future Post"
                >
                    <div className="left">
                        Schedule for Future Post
                    </div>

                    <div className="right schedule">
                        <div className="checkbox">
                            {SliderCheckbox({
                                value: enabled,
                                xdQaName: "Toggle Schedule",
                                onChange: () => scnv(field.state, ["enabled"], v=>!v),
                            })}
                        </div>
                        {enabled && <>
                            <div className="date-time" ref={field.ref}>
                                {(() => {

                                    const state = scopedState(field.state, ["date"]);
                                    return DatePicker2({

                                        state: convertState(state, {
                                            forward: (dStr) => dStr && df.parseDate(dStr),
                                            backward: (d) => d && df.formatDate(d),
                                        }),
                                        minDate: today(),
                                        maxDate: addDate(today(), 14),
                                        dateFormat: df,
                                        xdQaName: "Date",
                                        className: field.errCss,
                                        hasError: enabled && showErrors && !state.value,
                                    });
                                })()}

                                {(() => {
                                    const state = scopedState(field.state, ["time"]);
                                    return TimePicker2({
                                        state: state,
                                        className: field.errCss,
                                        hasError: enabled && showErrors && !state.value,
                                    });
                                })()}

                            </div>

                            {field.error?.failedValidator?.name === "future-time" ? (
                                <div className="error">
                                    Scheduled time must be in the future
                                </div>
                            ) : (!field.error) && (
                                <div {...{
                                    className: "confirm-time",
                                    "xd-qa-type": "label",
                                    "xd-qa-name": "Confirm Time",
                                }}>
                                    Set to {formatTime11(parseTime(field.state.value.time))} in local time{" "}
                                    {chain(
                                        df.parseDate(field.state.value.date),
                                        (_) => sameDate(_, today()) ? (
                                            "today"
                                        ) : sameDate(_, tomorrow()) ? (
                                            "tomorrow"
                                        ) : (
                                            "on " + parseDateFormat("MMM dd").formatDate(_)
                                        )
                                    )}
                                </div>
                            )}
                        </>}
                    </div>
                </div>
            );
        },
    }),
);

const df = parseDateFormat("yyyy-mm-dd");
